// board-reflow.ts — the board after a resize or a rotation (SC-60140). The vendored game lays itself out inside the
// resize event, but what that pass measures is only true once the browser has rendered a frame at the new size: after
// portrait → landscape the status line sat on the slider's thumb and the price bubble was gone, until a reload.
// Measured: one more pass in the same task, or in the first animation frame, changes nothing (three passes there do not
// either); a pass one rendered frame later draws exactly what a fresh load at that size draws.
//
// So after a resize the game is asked to lay out and paint again:
//   - one rendered frame later (an animation frame inside an animation frame), and
//   - once more shortly after the LAST resize event of a burst — a rotation fires several over a few hundred
//     milliseconds, and a hidden tab runs no animation frames at all.
// The game's own handle does both jobs (layout, paint); this only asks again. Installed once the game is driven, so the
// board is always there to lay out (a blocked boot tears it down and never gets here).
export interface ReflowableGame {
  layout(): void;
  paint(): void;
}

/** After the last resize event of a burst: long enough for a frame to have rendered, short enough to be unnoticed. */
export const SETTLE_MS = 150;

export function reflowOnResize(win: Window, game: ReflowableGame): void {
  let frame = 0;
  let settle: ReturnType<typeof setTimeout> | undefined;
  const reflow = (): void => {
    game.layout();
    game.paint();
  };
  win.addEventListener("resize", () => {
    win.cancelAnimationFrame(frame);
    frame = win.requestAnimationFrame(() => {
      frame = win.requestAnimationFrame(reflow);
    });
    clearTimeout(settle);
    settle = setTimeout(reflow, SETTLE_MS);
  });
}
